
import React, { useState, useEffect } from "react";
import ttsService, {
  GTSVoice,
  LanguageOption,
  IProject,
  CORE_LANGUAGE_OPTIONS,
  mergeLanguageOptions,
  OFFLINE_VOICE_LANGUAGE_CODES,
  STANDARD_FALLBACK_LANGUAGE_OPTIONS,
} from "../../services/ttsService";
import { ALL_MODELS, } from "../../utils/constants";
import {
  AdvancedOptions,
  BookConfig,
  DEFAULT_ADVANCED,
  DEFAULT_BOOK_CONFIG,
  DEFAULT_ELEVEN_ADVANCED,
  mergeAdvanced,
  mergeElevenAdvanced,
} from "../../utils/types";
import FormField from "../../components/FormField";

interface Props {
  project: IProject;
  onSave: (bookConfig: BookConfig) => void;
  onCancel: () => void;
}

/** Merge project.defaultSampling into advanced defaults (API may return numeric strings). */
function samplingFromProject(project: IProject): Partial<AdvancedOptions> {
  const s = project.defaultSampling;
  if (!s || typeof s !== "object") return {};
  const num = (v: unknown, fb: number) => {
    const n = typeof v === "number" ? v : Number(v);
    return Number.isFinite(n) ? n : fb;
  };
  return {
    temperature: num(s.temperature, DEFAULT_ADVANCED.temperature),
    topP: num(s.topP, DEFAULT_ADVANCED.topP),
    topK: num(s.topK, DEFAULT_ADVANCED.topK),
  };
}

const NewBookModal: React.FC<Props> = ({ project, onSave, onCancel }) => {
  const [config, setConfig] = useState<BookConfig>(() => ({
    ...DEFAULT_BOOK_CONFIG,
    advanced: mergeElevenAdvanced({
      ...DEFAULT_ELEVEN_ADVANCED,
      ...samplingFromProject(project),
    }),
  }));
  const [allVoices, setAllVoices] = useState<GTSVoice[]>([]);
  const [languages, setLanguages] = useState<LanguageOption[]>([]);
  const [filteredVoices, setFilteredVoices] = useState<GTSVoice[]>([]);
  const [loadingVoices, setLoadingVoices] = useState(true);
  const isEleven = config.platform === "ElevenLabs";

  useEffect(() => {
    const ds = project.defaultSampling;
    if (!ds || typeof ds !== "object") return;
    const patch = samplingFromProject(project);
    setConfig(prev => ({
      ...prev,
      advanced: mergeAdvanced({ ...prev.advanced, ...patch }),
    }));
    // Only re-apply when server-side sampling values change, not whole `project` referential identity
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    project._id,
    project.defaultSampling?.temperature,
    project.defaultSampling?.topP,
    project.defaultSampling?.topK,
  ]);

  useEffect(() => {
    setLoadingVoices(true);
    ttsService.getVoices(undefined, config.platform)
      .then(voices => {
        setAllVoices(voices);
        setLanguages(ttsService.buildLanguageList(voices));
        setLoadingVoices(false);
      })
      .catch(() => {
        setLanguages(mergeLanguageOptions(STANDARD_FALLBACK_LANGUAGE_OPTIONS, CORE_LANGUAGE_OPTIONS));
        setAllVoices([]);
        if (config.platform === "ElevenLabs") {
          setFilteredVoices([]);
        } else {
          setFilteredVoices([
            {
              name: "lv-LV-Chirp3-HD-Achernar",
              ssmlGender: "FEMALE",
              naturalSampleRateHertz: 24000,
              languageCodes: [...OFFLINE_VOICE_LANGUAGE_CODES],
            },
          ]);
        }
        setLoadingVoices(false);
      });
  }, [config.platform]);

  useEffect(() => {
    if (!allVoices.length) return;
    const nextVoices = ttsService.filterVoicesForSelection(allVoices, {
      model: config.model,
      platform: config.platform,
    });
    setFilteredVoices(nextVoices);
    setConfig(p => ({
      ...p,
      realVoice: nextVoices.some(v => v.name === p.realVoice) ? p.realVoice : (nextVoices[0]?.name || ""),
    }));
  }, [config.model, config.platform, allVoices]);

  const handle = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name === "platform") {
      const isElevenPlatform = value === "ElevenLabs";
      setConfig(p => ({
        ...p,
        platform: value,
        model: isElevenPlatform ? "" : (p.model || "Chirp3-HD"),
        advanced: isElevenPlatform
          ? mergeElevenAdvanced({
              stability: DEFAULT_ELEVEN_ADVANCED.stability,
              similarityBoost: DEFAULT_ELEVEN_ADVANCED.similarityBoost,
              style: DEFAULT_ELEVEN_ADVANCED.style,
              useSpeakerBoost: DEFAULT_ELEVEN_ADVANCED.useSpeakerBoost,
              temperature: p.advanced.temperature,
              topP: p.advanced.topP,
              topK: p.advanced.topK,
            })
          : mergeAdvanced({
              temperature: p.advanced.temperature ?? DEFAULT_ADVANCED.temperature,
              topP: p.advanced.topP ?? DEFAULT_ADVANCED.topP,
              topK: p.advanced.topK ?? DEFAULT_ADVANCED.topK,
              stability: DEFAULT_ADVANCED.stability,
              similarityBoost: DEFAULT_ADVANCED.similarityBoost,
              style: DEFAULT_ADVANCED.style,
              useSpeakerBoost: DEFAULT_ADVANCED.useSpeakerBoost,
            }),
      }));
      return;
    }
    setConfig(p => ({ ...p, [name]: value }));
  };

  const handleAdvanced = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    const boolValue = (e.target as HTMLInputElement).type === "checkbox"
      ? (e.target as HTMLInputElement).checked
      : undefined;
    setConfig((p) => ({
      ...p,
      advanced: {
        ...p.advanced,
        [name]:
          name === "audioEncoding" || name === "effectsProfileId"
            ? value
            : name === "useSpeakerBoost"
              ? Boolean(boolValue)
              : Number(value),
      },
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!config.title.trim()) return;
    if (!config.realVoice.trim()) return;
    onSave(config);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-box modal-box--wide">
        <div className="modal-title">New Book in "{project.projectName}"</div>
        <div className="modal-sub">A book is one audiobook title with its own voice and language settings.</div>

        <form onSubmit={handleSubmit}>
          <div className="modal-grid">

            <FormField label="Book Title">
              <input type="text" name="title" value={config.title} onChange={handle}
                placeholder="e.g. Harry Potter – Book 1" required autoFocus />
            </FormField>

            <FormField label="Author">
              <input type="text" name="author" value={config.author} onChange={handle}
                placeholder="e.g. J.K. Rowling" />
            </FormField>

            <FormField label="TTS Platform">
              <select name="platform" value={config.platform} onChange={handle}>
                <option value="ElevenLabs">ElevenLabs</option>
                <option value="Vertex AI">Vertex AI</option>
              </select>
            </FormField>

            {!isEleven && (
              <FormField label="Model" hint="Filters voice list.">
                <select name="model" value={config.model} onChange={handle}>
                  {ALL_MODELS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </FormField>
            )}

            <FormField label="Language" hint={loadingVoices ? "Loading…" : `${languages.length} languages`}>
              {loadingVoices
                ? <span className="cp-spinner">Fetching…</span>
                : <select name="language" value={config.language} onChange={handle}>
                  {languages.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
                </select>
              }
            </FormField>

            <FormField label="TTS Real Voice" hint={`${filteredVoices.length} voices`}>
              <select name="realVoice" value={config.realVoice} onChange={handle}
                required disabled={loadingVoices || filteredVoices.length === 0}>
                <option value="">{filteredVoices.length === 0 ? "No voices" : "Select voice…"}</option>
                {filteredVoices.map(v => (
                  <option key={v.name} value={v.name}>
                    {v.displayName || v.name} ({v.ssmlGender === "FEMALE" ? "F" : v.ssmlGender === "MALE" ? "M" : "N"}) {v.style ? `- ${v.style}` : ""}
                  </option>
                ))}
              </select>
            </FormField>

            <div className="modal-grid-span">
              <FormField label="Voice Label (custom)">
                <input type="text" name="voiceLabel" value={config.voiceLabel} onChange={handle}
                  placeholder="e.g. Narrator Anna" />
              </FormField>

              {isEleven ? (
                <>
                  <div className="modal-adv-3col">
                    <FormField label="Stability" hint="0 – 100">
                      <input type="number" name="stability" value={config.advanced.stability} onChange={handleAdvanced} min={0} max={100} step={1} />
                    </FormField>
                    <FormField label="Similarity" hint="0 – 100">
                      <input type="number" name="similarityBoost" value={config.advanced.similarityBoost} onChange={handleAdvanced} min={0} max={100} step={1} />
                    </FormField>
                    <FormField label="Style" hint="0 – 100">
                      <input type="number" name="style" value={config.advanced.style} onChange={handleAdvanced} min={0} max={100} step={1} />
                    </FormField>
                  </div>
                  <FormField label="Speaker Boost">
                    <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <input type="checkbox" name="useSpeakerBoost" checked={!!config.advanced.useSpeakerBoost} onChange={handleAdvanced} />
                      Enable speaker boost
                    </label>
                  </FormField>
                </>
              ) : (
                <div className="modal-adv-3col">
                  <FormField label="Temperature" hint="0.01 – 2.0">
                    <input
                      type="number"
                      name="temperature"
                      value={config.advanced.temperature}
                      onChange={handleAdvanced}
                      min={0.01}
                      max={2}
                      step={0.01}
                    />
                  </FormField>
                  <FormField label="Top-P" hint="0 – 1">
                    <input
                      type="number"
                      name="topP"
                      value={config.advanced.topP}
                      onChange={handleAdvanced}
                      min={0}
                      max={1}
                      step={0.01}
                    />
                  </FormField>
                  <FormField label="Top-K" hint="1+">
                    <input
                      type="number"
                      name="topK"
                      value={config.advanced.topK}
                      onChange={handleAdvanced}
                      min={1}
                      step={1}
                    />
                  </FormField>
                </div>
              )}
            </div>

            <FormField label="Additional prompt" hint="Optional — appended after built‑in narrator instructions.">
              <textarea name="styleInstructions" value={config.styleInstructions} onChange={handle}
                placeholder="Extra tone or delivery notes…" rows={2} />
            </FormField>

          </div>

          <div className="modal-actions">
            <button type="button" className="modal-btn modal-btn--cancel" onClick={onCancel}>Cancel</button>
            <button type="submit" className="modal-btn modal-btn--save">Create Book</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewBookModal;